"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { SupportAPI } from "@/lib/api";
import { FillAppError } from "@/lib/FillAppError";

/* Support ticket form validation schema */
const supportTicketSchema = z.object({
  subject: z
    .string()
    .min(3, "Subject must be at least 3 characters")
    .max(200, "Subject must not exceed 200 characters"),
  description: z
    .string()
    .min(1, "Description is required")
    .max(5000, "Description must not exceed 5000 characters"),
  category: z.string().min(1, "Please select a category"),
  email: z.string().email("Please enter a valid email address"),
});

type SupportTicketForm = z.infer<typeof supportTicketSchema>;

/* Support category options */
const supportCategories = [
  { value: "Billing Issues", label: "Billing Issues" },
  { value: "Bug Reports", label: "Bug Reports" },
  { value: "Feature Request", label: "Feature Request" },
  { value: "Feedback", label: "Feedback" },
  { value: "Account Issues", label: "Account Issues" },
  { value: "Partnership", label: "Partnership" },
  { value: "Other", label: "Other" },
];

export default function HelpPage() {
  const { user } = useAuth();
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<SupportTicketForm>({
    subject: "",
    description: "",
    category: "",
    email: user?.email || "",
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof SupportTicketForm, string>>
  >({});

  const handleInputChange = (field: keyof SupportTicketForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    try {
      supportTicketSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Partial<Record<keyof SupportTicketForm, string>> = {};
        error.issues.forEach((err: z.ZodIssue) => {
          const field = err.path[0] as keyof SupportTicketForm;
          newErrors[field] = err.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await SupportAPI.createSupportTicket({
        subject: formData.subject,
        description: formData.description,
        category: formData.category,
      });

      setIsSubmitted(true);
      toast.success("Support ticket submitted successfully!");
    } catch (error) {
      console.error("Error submitting support ticket:", error);
      if (error instanceof FillAppError) {
        toast.error(`Failed to submit support ticket: ${error.message}`);
      } else {
        toast.error("Failed to submit support ticket. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex justify-center">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-primary">Thank You!</CardTitle>
            <CardDescription>
              We&apos;ve received your support request and will contact you via
              email as soon as possible.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Our support team typically responds within 24 hours during
              business days. You&apos;ll receive a confirmation email shortly
              with your ticket details.
            </p>
            <Button
              onClick={() => router.push("/dashboard")}
              className="w-full sm:w-auto"
            >
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-2xl">Get Help & Support</CardTitle>
          <CardDescription>
            Need assistance? Send us a message and our team will get back to you
            as soon as possible.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="your.email@example.com"
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            {/* Category Field */}
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                onValueChange={(value) => handleInputChange("category", value)}
                value={formData.category}
              >
                <SelectTrigger aria-invalid={!!errors.category}>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {supportCategories.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category && (
                <p className="text-sm text-destructive">{errors.category}</p>
              )}
            </div>

            {/* Subject Field */}
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => handleInputChange("subject", e.target.value)}
                placeholder="Brief description of your issue or request"
                aria-invalid={!!errors.subject}
              />
              {errors.subject && (
                <p className="text-sm text-destructive">{errors.subject}</p>
              )}
            </div>

            {/* Description Field */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Please provide detailed information about your issue, question, or feedback..."
                className="min-h-32 resize-none"
                aria-invalid={!!errors.description}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {formData.description.length}/5000 characters
              </p>
            </div>

            {/* Submit Button */}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Support Request"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
